<template>
  <t-cascader
    v-model="selectedValue"
    class="rule-variable-selector"
    :options="options"
    :placeholder="placeholder || t('选择变量')"
    :empty="t('暂无数据')"
    :check-strictly="false"
    trigger="hover"
    @change="handleChange"
  >
    <template #option="{ item }">
      <div class="variable-selector-option">
        <span class="variable-selector-option-label">{{ item.label }}</span>
      </div>
    </template>
  </t-cascader>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { t } from "@/utils/i18n";
import { JudgeType } from "@/types/conditionJudgeNodeType";

// 类型定义
interface Variable {
  id?: string;
  label: string;
  type: JudgeType | string;
  children?: Variable[];
  value: string | JudgeType;
}

// Props 类型定义
interface Props {
  modelValue: string;
  variables?: Variable[];
  placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "",
  variables: () => [],
});

// Emits 类型定义
const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
}>();

// 响应式状态
const selectedValue = ref<string | string[]>(props.modelValue);

// 监听 modelValue 变化
watch(
  () => props.modelValue,
  (newValue) => {
    // 如果 modelValue 是字符串，需要转换为级联选择器需要的格式
    // 级联选择器需要数组格式，但我们可以直接使用字符串，它会自动处理
    selectedValue.value = newValue as any;
  },
);

// 优化计算属性：过滤掉空的自定义变量组
const options = computed(() => {
  // 过滤掉空的自定义变量组（value === JudgeType.CUSTOM 且没有 children 的项）
  return props.variables.filter((v) => {
    if (
      v.value === JudgeType.CUSTOM &&
      (!v.children || v.children.length === 0)
    ) {
      return false;
    }
    return true;
  });
});

// 处理选择变化
const handleChange = (value: string | string[]): void => {
  if (!value) return;

  // 级联选择器返回的是数组路径，例如：[2, "${2-SystemSessionID}"]
  // 我们需要取最后一个值作为最终选中的值
  const finalValue = Array.isArray(value) ? value[value.length - 1] : value;

  if (!finalValue) return;

  selectedValue.value = finalValue as any;
  emit("update:modelValue", finalValue);
  emit("change", finalValue);
};
</script>

<style lang="less" scoped>
// 使用 :deep() 确保样式能够穿透到级联选择器的内部元素
.variable-selector-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-width: 0; // 允许 flex 容器收缩，使子元素的 text-overflow 生效

  .variable-selector-option-label {
    display: block;
    flex: 1;
    min-width: 0; // 允许 flex 子元素收缩
    max-width: 100%; // 确保不超过父容器宽度
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
